import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';

const emptyContact = () => ({ name: '', title: '', email: '', phone: '' });

const buildInitialForm = (customer) => ({
  customerNumber: customer?.customerNumber || '',
  name: customer?.name || '',
  shortName: customer?.shortName || '',
  country: customer?.country || '',
  level: customer?.level || '',
  source: customer?.source || '',
  paymentMethod: customer?.paymentMethod || '',
  address: customer?.address || '',
  deal: customer?.deal || false,
  notes: customer?.notes || '',
  assignedSalesId: customer?.assignedSalesId?._id || customer?.assignedSalesId || '',
  contacts: customer?.contacts?.length ? customer.contacts.map((c) => ({ ...c })) : [emptyContact()],
});

const CustomerModal = ({ open, mode, customer, onClose, onSave, onDelete, onModeChange, currentUser }) => {
  const [form, setForm] = useState(() => buildInitialForm(null));
  const [currentMode, setCurrentMode] = useState(mode);
  const [salesOptions, setSalesOptions] = useState([]);

  useEffect(() => { setCurrentMode(mode); }, [mode]);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((users) => {
        const list = Array.isArray(users) ? users : [];
        setSalesOptions(list.filter((u) => u.isActive && (u.role === 'sales' || u.role === 'manager')));
      })
      .catch(() => setSalesOptions([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      if (customer && (mode === 'view' || mode === 'edit')) {
        setForm(buildInitialForm(customer));
      } else {
        setForm(buildInitialForm(null));
      }
    }
  }, [open, customer, mode]);

  const isView = currentMode === 'view';

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateContact = (idx, field, value) => {
    setForm((prev) => {
      const contacts = [...prev.contacts];
      contacts[idx] = { ...contacts[idx], [field]: value };
      return { ...prev, contacts };
    });
  };

  const addContact = () => setForm((prev) => ({ ...prev, contacts: [...prev.contacts, emptyContact()] }));

  const removeContact = (idx) => {
    if (form.contacts.length <= 1) return;
    setForm((prev) => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== idx) }));
  };

  const handleSave = () => {
    if (!form.customerNumber.trim()) { alert('客戶編號為必填欄位'); return; }
    if (!form.name.trim()) { alert('客戶名稱為必填欄位'); return; }
    onSave({ ...form, customerNumber: form.customerNumber.trim() });
  };

  const handleDelete = () => {
    if (!customer) return;
    if (window.confirm(`確定要刪除「${customer.name}」嗎？此操作無法復原。`)) {
      onDelete(customer._id);
    }
  };

  const enableEdit = () => { setCurrentMode('edit'); onModeChange('edit'); };

  const cancelEdit = () => {
    if (customer) {
      setCurrentMode('view');
      onModeChange('view');
      setForm(buildInitialForm(customer));
    } else {
      onClose();
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  if (!open) return null;

  const title = currentMode === 'add' ? '👥 新增客戶' : currentMode === 'view' ? '👤 客戶詳情' : '✏️ 編輯客戶';

  return (
    <div className={`customer-modal-overlay${open ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="customer-modal">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} title="關閉">✕</button>
        </div>

        <div className={`modal-body${isView ? ' view-mode' : ''}`}>
          {/* Meta info row */}
          <div className="meta-info-row">
            {customer && (
              <div className="meta-item">
                <span className="meta-label">📅 建檔日期：</span>
                <span className="meta-value">{formatDate(customer.createdAt)}</span>
              </div>
            )}
            <div className="meta-deal-group">
              <span className="meta-label">成交狀態：</span>
              <div className="toggle-group">
                <button type="button" className={`toggle-btn deal-yes${form.deal ? ' active' : ''}`} onClick={() => !isView && updateField('deal', true)}>✓ 已成交</button>
                <button type="button" className={`toggle-btn deal-no${!form.deal ? ' active' : ''}`} onClick={() => !isView && updateField('deal', false)}>— 未成交</button>
              </div>
            </div>
          </div>

          {/* Customer Level */}
          <div className="meta-info-row">
            <div className="meta-deal-group">
              <span className="meta-label">客戶分級：</span>
              <div className="toggle-group">
                {[
                  { key: '', label: '未分級', cls: 'level-none' },
                  { key: 'A', label: 'A級', cls: 'level-a' },
                  { key: 'B', label: 'B級', cls: 'level-b' },
                  { key: 'C', label: 'C級', cls: 'level-c' },
                  { key: 'D', label: 'D級', cls: 'level-d' },
                ].map((l) => (
                  <button key={l.key} type="button" className={`toggle-btn ${l.cls}${form.level === l.key ? ' active' : ''}`} onClick={() => !isView && updateField('level', l.key)}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 1: 客戶編號｜客戶國別｜負責業務｜客戶來源 */}
          <div className="form-grid form-grid-4" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group">
              <label>客戶編號 <span className="required-mark">*</span></label>
              <input type="text" className="form-control" placeholder="請輸入客戶編號" value={form.customerNumber} onChange={(e) => updateField('customerNumber', e.target.value)} disabled={isView} />
            </div>
            <div className="form-group">
              <label>客戶國別</label>
              <input type="text" className="form-control" placeholder="如：日本、美國、US..." value={form.country} onChange={(e) => updateField('country', e.target.value)} disabled={isView} />
            </div>
            <div className="form-group">
              <label>負責業務</label>
              <select className="form-control" value={form.assignedSalesId || ''} onChange={(e) => updateField('assignedSalesId', e.target.value)} disabled={isView}>
                <option value="">未指派</option>
                {salesOptions.map((u) => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>客戶來源</label>
              <input type="text" className="form-control" placeholder="如：展覽、官網、客戶介紹..." value={form.source} onChange={(e) => updateField('source', e.target.value)} disabled={isView} />
            </div>
          </div>

          {/* Row 2: 客戶名稱｜客戶簡稱 */}
          <div className="form-grid form-grid-2" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group">
              <label>客戶名稱 <span className="required-mark">*</span></label>
              <input type="text" className="form-control" placeholder="請輸入客戶公司名稱" value={form.name} onChange={(e) => updateField('name', e.target.value)} disabled={isView} />
            </div>
            <div className="form-group">
              <label>客戶簡稱</label>
              <input type="text" className="form-control" placeholder="請輸入客戶簡稱（選填）" value={form.shortName} onChange={(e) => updateField('shortName', e.target.value)} disabled={isView} />
            </div>
          </div>

          {/* Row 3: 付款方式（需求 R27：位於客戶名稱與地址之間，由客戶自行輸入）*/}
          <div className="form-grid" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group full-width">
              <label>付款方式</label>
              <input type="text" className="form-control" placeholder="如：月結30天、貨到付款、T/T..." value={form.paymentMethod} onChange={(e) => updateField('paymentMethod', e.target.value)} disabled={isView} />
            </div>
          </div>

          {/* Row 4: Address */}
          <div className="form-grid" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group full-width">
              <label>地址</label>
              <input type="text" className="form-control" placeholder="請輸入完整地址（含國家、城市、街道）" value={form.address} onChange={(e) => updateField('address', e.target.value)} disabled={isView} />
            </div>
          </div>

          {/* Contacts */}
          <div className="contacts-section" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="contacts-section-header">
              <span className="contacts-section-label">👤 聯絡人資訊</span>
              {!isView && <button type="button" className="contacts-add-btn" onClick={addContact}>+ 新增聯絡人</button>}
            </div>
            <div>
              {form.contacts.map((c, idx) => (
                <div key={idx} className="contact-row">
                  <div className="contact-row-header">
                    <span className="contact-row-num">聯絡人 #{idx + 1}</span>
                    {!isView && (
                      <button type="button" className="contact-remove-btn" onClick={() => removeContact(idx)} disabled={form.contacts.length <= 1} title="移除此聯絡人">✕</button>
                    )}
                  </div>
                  <div className="contact-row-fields">
                    <div className="form-group">
                      <label>聯絡人</label>
                      <input type="text" className="form-control" placeholder="姓名" value={c.name} onChange={(e) => updateContact(idx, 'name', e.target.value)} disabled={isView} />
                    </div>
                    <div className="form-group">
                      <label>職稱</label>
                      <input type="text" className="form-control" placeholder="如：採購經理" value={c.title} onChange={(e) => updateContact(idx, 'title', e.target.value)} disabled={isView} />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" className="form-control" placeholder="example@company.com" value={c.email} onChange={(e) => updateContact(idx, 'email', e.target.value)} disabled={isView} />
                    </div>
                    <div className="form-group">
                      <label>聯絡電話</label>
                      <input type="tel" className="form-control" placeholder="+886 2-XXXX-XXXX" value={c.phone} onChange={(e) => updateContact(idx, 'phone', e.target.value)} disabled={isView} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="form-grid">
            <div className="form-group full-width">
              <label>備註</label>
              <textarea className="form-control" rows={3} placeholder="輸入客戶相關備註、特殊需求或跟進事項..." value={form.notes} onChange={(e) => updateField('notes', e.target.value)} disabled={isView} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div>
            {currentMode === 'edit' && customer && currentUser?.role === 'manager' && (
              <button className="btn btn-danger" onClick={handleDelete}>🗑️ 刪除此客戶</button>
            )}
          </div>
          <div className="modal-footer-right">
            {isView ? (
              <>
                <button className="btn btn-outline" onClick={onClose}>關閉</button>
                <button className="btn btn-primary" onClick={enableEdit}>✏️ 編輯</button>
              </>
            ) : (
              <>
                <button className="btn btn-outline" onClick={cancelEdit}>取消</button>
                <button className="btn btn-primary" onClick={handleSave}>✓ 儲存</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerModal;
